import fetchWithAuth, { getCore as get, postCore as post, delCore as del, putCore as put } from "../../utils/httpInterceptor";

class TenantUserService {
  // Store tenantId for instance methods
  constructor(tenantId = null, options = {}) {
    this.tenantId = tenantId;
    this.flowBpmAvailable = options.flowBpmAvailable === true;
  }

  // Static methods with explicit tenantId
  static async list(tenantId) {
    return await get(`/api/tenants/${tenantId}/users`);
  }

  static async create(tenantId, payload) {
    return await post(`/api/tenants/${tenantId}/users`, payload);
  }

  static async update(tenantId, userId, payload) {
    return await put(`/api/tenants/${tenantId}/users/${userId}`, payload);
  }

  static async listDataAreas(tenantId, userId) {
    return await get(`/api/tenants/${tenantId}/users/${userId}/dataareas`);
  }

  static async updateDataAreas(tenantId, userId, dataAreaIds) {
    return await put(`/api/tenants/${tenantId}/users/${userId}/dataareas`, {
      dataAreaIds: Array.isArray(dataAreaIds) ? dataAreaIds : [],
    });
  }

  static async getPreferences(tenantId, userId) {
    return await get(`/api/tenants/${tenantId}/users/${userId}/preferences`);
  }

  static async updatePreferences(tenantId, userId, preferences) {
    return await put(`/api/tenants/${tenantId}/users/${userId}/preferences`, preferences || {});
  }

  static async delete(tenantId, userId) {
    return await del(`/api/tenants/${tenantId}/users/${userId}`);
  }

  /**
   * Resetea el MFA de otro usuario (móvil perdido, etc). El backend ya exige
   * el permiso system.users.update; el frontend solo muestra el botón donde
   * ya se muestran otras acciones de administración.
   * POST /api/tenants/{tenantId}/users/{userId}/mfa/reset -> 204 No Content
   */
  static async resetMfa(tenantId, userId) {
    return await post(`/api/tenants/${tenantId}/users/${userId}/mfa/reset`);
  }

  static async syncFlowBpmUser(tenantId, user) {
    const response = await fetchWithAuth("/api/flow-bpm/users/sync", {
      method: "POST",
      body: JSON.stringify({
        tenantId,
        userId: user?.userId || user?.id,
        subject: user?.subject,
        email: user?.email,
        displayName: user?.displayName,
        role: user?.role,
        enabled: user?.enabled !== false,
        bpmUserEnabled: user?.bpmUserEnabled === true,
      }),
    });
    return response.json();
  }

  // Instance methods (use tenantId from constructor)
  async getAll(page = 0, pageSize = 100, filters = {}) {
    const data = await TenantUserService.list(this.tenantId);
    // Map userId to id for CRUD operations compatibility
    const mappedData = Array.isArray(data) ? data.map(item => ({
      ...item,
      id: item.userId
    })) : [];
    return { 
      content: mappedData, 
      totalElements: mappedData.length,
      totalPages: 1 
    };
  }

  async create(data) {
    const { dataAreaIds, userPreferences, ...payload } = data || {};
    const response = await TenantUserService.create(this.tenantId, payload);
    if (Array.isArray(dataAreaIds) && response?.userId) {
      await TenantUserService.updateDataAreas(this.tenantId, response.userId, dataAreaIds);
    }
    if (userPreferences && response?.userId) {
      await TenantUserService.updatePreferences(this.tenantId, response.userId, userPreferences);
    }
    if (this.flowBpmAvailable && response?.userId) {
      await TenantUserService.syncFlowBpmUser(this.tenantId, response);
    }
    // Map userId to id for CRUD operations compatibility
    return {
      ...response,
      id: response.userId,
      dataAreaIds: Array.isArray(dataAreaIds) ? dataAreaIds : [],
      userPreferences: userPreferences || null,
    };
  }

  async update(id, data) {
    const { dataAreaIds, userPreferences, ...payload } = data || {};
    const response = await TenantUserService.update(this.tenantId, id, payload);
    if (Array.isArray(dataAreaIds)) {
      await TenantUserService.updateDataAreas(this.tenantId, id, dataAreaIds);
    }
    if (userPreferences) {
      await TenantUserService.updatePreferences(this.tenantId, id, userPreferences);
    }
    if (this.flowBpmAvailable && response?.userId) {
      await TenantUserService.syncFlowBpmUser(this.tenantId, response);
    }
    return {
      ...response,
      id: response?.userId || id,
      dataAreaIds: Array.isArray(dataAreaIds) ? dataAreaIds : [],
      userPreferences: userPreferences || null,
    };
  }

  async delete(id) {
    return await TenantUserService.delete(this.tenantId, id);
  }

  async resetMfa(id) {
    return await TenantUserService.resetMfa(this.tenantId, id);
  }

  async updateEnabled(id, data) {
    // Not directly supported, use update as fallback
    return await TenantUserService.update(this.tenantId, id, data);
  }

  async listDataAreas(userId) {
    return await TenantUserService.listDataAreas(this.tenantId, userId);
  }

  async updateDataAreas(userId, dataAreaIds) {
    return await TenantUserService.updateDataAreas(this.tenantId, userId, dataAreaIds);
  }

  async getPreferences(userId) {
    return await TenantUserService.getPreferences(this.tenantId, userId);
  }

  async updatePreferences(userId, preferences) {
    return await TenantUserService.updatePreferences(this.tenantId, userId, preferences);
  }
}

export default TenantUserService;
